import { Injectable, inject, signal } from '@angular/core';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { MsalService } from '@azure/msal-angular';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { Notification, SubmissionUpdatedEvent } from '../models/notification.models';

export interface NotificationHubHandlers {
  /** Fired for every new notification pushed to the current user. */
  onNotification: (notification: Notification) => void;
  /** Fired when an existing notification is updated (e.g. Warning → Success on resolution). */
  onNotificationUpdated: (notification: Notification) => void;
  /** Fired when a notification is deleted. */
  onNotificationDeleted: (payload: { notificationId: number }) => void;
  /** Fired after an automatic reconnect — refetch to catch anything missed while offline. */
  onReconnected: () => void;
}

/**
 * Owns the SignalR connection to the API's notification hub. Connection lifecycle only —
 * notification state lives in NotificationStateService, which registers handlers here.
 */
@Injectable({ providedIn: 'root' })
export class NotificationHubService {
  private readonly msal = inject(MsalService);
  private connection: HubConnection | null = null;

  readonly connected = signal(false);

  private readonly submissionUpdatedSubject = new Subject<SubmissionUpdatedEvent>();
  /** Server-side changes to a submission (e.g. participant import linked its rows). Any screen
   * showing that upload can subscribe and refresh; the stream is hot and never completes. */
  readonly submissionUpdated$: Observable<SubmissionUpdatedEvent> = this.submissionUpdatedSubject.asObservable();

  /** The hub lives on the API host, one level above the /api base path. */
  private readonly hubUrl = `${environment.apiUrl.replace(/\/api\/?$/, '')}/hubs/notifications`;

  /** Delays between initial-start attempts; withAutomaticReconnect only covers drops after a successful start. */
  private static readonly startRetryDelaysMs = [2000, 5000, 10000];

  async connect(handlers: NotificationHubHandlers): Promise<void> {
    if (this.connection) return;

    this.connection = new HubConnectionBuilder()
      .withUrl(
        this.hubUrl,
        environment.authEnabled ? { accessTokenFactory: () => this.acquireToken() } : {},
      )
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    this.connection.on('ReceiveNotification', handlers.onNotification);
    this.connection.on('NotificationUpdated', handlers.onNotificationUpdated);
    this.connection.on('NotificationDeleted', handlers.onNotificationDeleted);
    this.connection.on('SubmissionUpdated', (payload: SubmissionUpdatedEvent) =>
      this.submissionUpdatedSubject.next(payload),
    );
    this.connection.onreconnecting(() => this.connected.set(false));
    this.connection.onclose(() => this.connected.set(false));
    this.connection.onreconnected(() => {
      this.connected.set(true);
      handlers.onReconnected();
    });

    await this.startWithRetry();
  }

  private async startWithRetry(): Promise<void> {
    for (let attempt = 0; ; attempt++) {
      try {
        await this.connection!.start();
        this.connected.set(true);
        return;
      } catch (error) {
        if (attempt >= NotificationHubService.startRetryDelaysMs.length) {
          console.error('Notification hub connection failed; live updates disabled', error);
          return;
        }
        await new Promise((resolve) =>
          setTimeout(resolve, NotificationHubService.startRetryDelaysMs[attempt]),
        );
      }
    }
  }

  private async acquireToken(): Promise<string> {
    try {
      const instance = this.msal.instance;
      const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
      if (!account) return '';
      const result = await instance.acquireTokenSilent({
        scopes: [...environment.apiConfig.scopes],
        account,
      });
      return result.accessToken;
    } catch (error) {
      console.error('Failed to acquire token for notification hub', error);
      return '';
    }
  }
}
